// The read-only chat transcript — `/analytics/sessions/$sessionId`.
//
// Everything else under `/analytics` renders counts about a conversation. This
// page renders the conversation. It exists so an operator debugging a session
// can read what actually happened instead of inferring it from an action
// ledger, and every design choice on it follows from the fact that the words
// belong to someone else:
//
//   - There is no compose box, no retry, no resume. The page cannot write.
//   - It says out loud, in the page rather than in a doc, that the read was
//     logged. A permission that is invisible to the person using it gets used
//     carelessly.
//   - It states what the projection left out, so nobody reads a short
//     transcript as "nothing else happened".
//
// Access is server-owned, exactly as the dashboard's is: the route loader only
// checks that someone is signed in, and a 403 becomes a rendered state. No role
// lives in the cookie, so a client-side gate here would be decoration.

import type { JSX } from "react";

import type { SessionTranscriptEntry } from "@loom/shared/analytics";

import { useFormatDate } from "../i18n/format.js";
import { useT, type Translator } from "../i18n/index.js";

import { useSessionTranscript } from "./useAnalytics.js";

interface TranscriptPageProps {
  readonly sessionId: string;
  readonly onBack: () => void;
}

const speakerLabel = (t: Translator, kind: SessionTranscriptEntry["kind"]): string =>
  t(`analytics.transcript.speaker.${kind}`);

/**
 * One turn.
 *
 * A `tool` row is a single line — name plus whether it worked — because that is
 * all the projection carries. A tool whose output never arrived shows no
 * outcome rather than an invented one: the call really did leave without a
 * result, and that gap is often the thing being debugged.
 */
const Entry = ({
  entry,
  time,
}: {
  entry: SessionTranscriptEntry;
  time: (when: string) => string;
}): JSX.Element => {
  const t = useT();
  return (
    <li className={`transcript-entry transcript-${entry.kind}`}>
      <div className="transcript-meta">
        <span className="transcript-speaker">{speakerLabel(t, entry.kind)}</span>
        <span className="transcript-time">{time(entry.at)}</span>
        {entry.issueIid !== null ? (
          <span className="transcript-issue">#{entry.issueIid}</span>
        ) : null}
      </div>
      {entry.kind === "tool" ? (
        <div className="transcript-tool">
          <code>{entry.name}</code>
          {entry.outcome !== null ? (
            <span className={`transcript-outcome transcript-outcome-${entry.outcome}`}>
              {entry.outcome}
            </span>
          ) : null}
        </div>
      ) : (
        <div className="transcript-text">
          {entry.name !== null ? <code>{entry.name}</code> : null}
          {entry.text}
        </div>
      )}
    </li>
  );
};

export const TranscriptPage = ({
  sessionId,
  onBack,
}: TranscriptPageProps): JSX.Element => {
  const t = useT();
  const time = useFormatDate({ dateStyle: "short", timeStyle: "medium" });
  const { status, data, error } = useSessionTranscript(sessionId);

  const shell = (body: JSX.Element): JSX.Element => (
    <div className="loom-dashboard analytics-page transcript-page">
      <header className="analytics-head">
        <button className="analytics-back" onClick={onBack} type="button">
          ← {t("analytics.transcript.back")}
        </button>
        <div>
          <h1 className="analytics-title">{t("analytics.transcript.title")}</h1>
          <p className="analytics-subtitle analytics-mono">{sessionId}</p>
        </div>
      </header>
      {body}
    </div>
  );

  if (status === "loading") {
    return shell(
      <p className="analytics-muted">{t("analytics.transcript.loading")}</p>,
    );
  }
  if (status === "forbidden") {
    return shell(
      <div className="analytics-state analytics-denied">
        <h2>{t("analytics.transcript.forbidden.title")}</h2>
        <p>{t("analytics.transcript.forbidden.body")}</p>
      </div>,
    );
  }
  if (status === "notAvailable") {
    return shell(
      <div className="analytics-state">
        <h2>{t("analytics.transcript.notAvailable.title")}</h2>
        <p>{t("analytics.transcript.notAvailable.body")}</p>
      </div>,
    );
  }
  if (status === "error" || data === null) {
    return shell(
      <div className="analytics-state analytics-state-error">
        <h2>{t("analytics.transcript.error.title")}</h2>
        <p>{error}</p>
      </div>,
    );
  }

  return shell(
    <>
      <p className="transcript-notice">{t("analytics.transcript.notice")}</p>
      <p className="analytics-named-caution">{t("analytics.transcript.scope")}</p>
      <p className="analytics-muted">
        {t("analytics.transcript.counts", {
          entries: data.entries.length,
          events: data.eventCount,
        })}
      </p>
      {data.capped ? (
        <p className="analytics-muted">{t("analytics.transcript.capped")}</p>
      ) : null}
      {data.entries.length === 0 ? (
        <p className="analytics-muted">{t("analytics.transcript.empty")}</p>
      ) : (
        <ol className="transcript-list">
          {data.entries.map((e) => (
            // `seq` is the event log's own ordering and is unique within a
            // session — a stable key that survives a refetch, unlike the index.
            <Entry key={e.seq} entry={e} time={time} />
          ))}
        </ol>
      )}
    </>,
  );
};
